// src/components/NotificationBell.jsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, Check, CheckCheck, Settings, X } from "lucide-react";
import { useNotifications } from "../contexts/NotificationContext";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { pressable } from "../utils/pressable";
import { isNative } from "../lib/platform";

// Ask once people have notifications to look at, not on first load: the ask
// makes sense once they can see what they'd be pinged about. "Not now" waits
// 30 days. In the app, the native permission prompt; in a browser, web push.
const PUSH_ASK_KEY = "sift:push-ask";
const PUSH_ASK_SNOOZE_MS = 30 * 24 * 3600 * 1000;

function readPushAsk() {
  try {
    return JSON.parse(localStorage.getItem(PUSH_ASK_KEY) || "null");
  } catch {
    return null;
  }
}

function writePushAsk(value) {
  try {
    localStorage.setItem(PUSH_ASK_KEY, JSON.stringify(value));
  } catch {
    // Private mode: the ask just comes back next time.
  }
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
function formatTimeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

/**
 * Get notification type styling
 */
function getTypeStyles(type) {
  switch (type) {
    case "warning":
      return "border-l-yellow-500 bg-yellow-500/5";
    case "error":
      return "border-l-red-500 bg-red-500/5";
    case "success":
      return "border-l-green-500 bg-green-500/5";
    default:
      return "border-l-blue-500 bg-blue-500/5";
  }
}

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
  } = useNotifications();
  
  const {
    permission,
    subscription,
    isSupported,
    loading: pushLoading,
    subscribe,
    unsubscribe,
  } = usePushNotifications();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowSettings(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle push toggle
  const handlePushToggle = async () => {
    if (subscription) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  const isPushEnabled = !!subscription;

  const [pushAsk, setPushAsk] = useState(readPushAsk);
  const [askBusy, setAskBusy] = useState(false);
  const [askNote, setAskNote] = useState("");
  const native = isNative();
  const askSnoozed = pushAsk?.until && Date.now() < pushAsk.until;
  const showPushAsk =
    notifications.length > 0 &&
    !pushAsk?.done &&
    !askSnoozed &&
    (native || (isSupported && permission === "default" && !subscription));

  const turnOnPush = async () => {
    setAskBusy(true);
    setAskNote("");
    try {
      let ok = false;
      if (native) {
        const { registerForPush } = await import("../lib/push");
        const r = await registerForPush();
        ok = !!r?.ok;
      } else {
        const r = await subscribe();
        ok = !!r?.success;
      }
      if (ok) {
        const next = { done: true };
        writePushAsk(next);
        setPushAsk(next);
      } else {
        setAskNote(native
          ? "That didn't work. Check that notifications are allowed for Sift in your phone's settings."
          : "That didn't work in this browser. You can try again from the settings gear.");
      }
    } finally {
      setAskBusy(false);
    }
  };

  const snoozePushAsk = () => {
    const next = { until: Date.now() + PUSH_ASK_SNOOZE_MS };
    writePushAsk(next);
    setPushAsk(next);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) fetchNotifications();
        }}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
      >
        <Bell className="w-5 h-5" />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-xs font-bold bg-red-500 text-white rounded-full px-1">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-surface border border-line rounded-xl shadow-sift-lg overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-line">
            <h3 className="font-semibold text-ink">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-brand hover:text-brand-hover flex items-center gap-1"
                  title="Mark all as read"
                >
                  <CheckCheck className="w-4 h-4" />
                  <span className="hidden sm:inline">Mark all read</span>
                </button>
              )}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-1.5 rounded hover:bg-sunken transition-colors text-secondary ${showSettings ? "bg-sunken" : ""}`}
                title="Notification settings"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="px-4 py-3 border-b border-line bg-sunken">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-ink">Push Notifications</p>
                  <p className="text-xs text-secondary">
                    {!isSupported 
                      ? "Not supported in this browser"
                      : permission === "denied"
                      ? "Blocked by browser settings"
                      : isPushEnabled
                      ? "Enabled"
                      : "Disabled"}
                  </p>
                </div>
                <button role="switch" aria-checked={isPushEnabled} aria-label="Push notifications"
                  onClick={handlePushToggle}
                  disabled={!isSupported || permission === "denied" || pushLoading}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    isPushEnabled ? "bg-brand" : "bg-line-strong"
                  } ${(!isSupported || permission === "denied") ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      isPushEnabled ? "translate-x-6" : ""
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          {showPushAsk && (
            <div className="px-4 py-3 border-b border-line bg-brand-tint">
              <div className="flex items-start gap-3">
                <BellRing className="w-5 h-5 text-brand shrink-0 mt-0.5" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">Get a ping when someone messages you?</p>
                  <p className="text-xs text-secondary mt-0.5">
                    Turn on notifications for this device so you don't miss a message or a signature.
                  </p>
                  {askNote && <p className="text-xs text-gold-ink mt-1">{askNote}</p>}
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={turnOnPush}
                      disabled={askBusy}
                      className="rounded-pill bg-brand text-white hover:bg-brand-hover px-3 py-1 text-xs font-medium disabled:opacity-60"
                    >
                      {askBusy ? "Turning on..." : "Turn on"}
                    </button>
                    <button
                      onClick={snoozePushAsk}
                      disabled={askBusy}
                      className="rounded-pill px-3 py-1 text-xs text-secondary hover:text-ink"
                    >
                      Not now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-secondary">
                <div className="animate-spin w-6 h-6 border-2 border-line-strong border-t-brand rounded-full mx-auto mb-2" />
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-secondary">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-4 py-3 border-b border-line/70 hover:bg-sunken transition-colors cursor-pointer border-l-2 ${
                    getTypeStyles(notification.type)
                  } ${!notification.is_read ? "bg-brand-tint/40" : ""}`}
                  {...pressable(() => {
                    if (!notification.is_read) {
                      markAsRead(notification.id);
                    }
                    if (notification.link) {
                      setIsOpen(false);
                      navigate(notification.link);
                    }
                  })}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm text-ink ${!notification.is_read ? "font-semibold" : ""}`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-secondary mt-0.5 line-clamp-2">
                        {notification.body}
                      </p>
                      <p className="text-xs text-faint mt-1">
                        {formatTimeAgo(notification.created_at)}
                      </p>
                    </div>
                    {!notification.is_read && (
                      <span className="w-2 h-2 bg-brand rounded-full flex-shrink-0 mt-1.5" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-line text-center">
              <button
                onClick={() => {
                  setIsOpen(false);
                  // Navigate to notifications page if you have one
                  // navigate("/notifications");
                }}
                className="text-xs text-brand hover:text-brand-hover"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}